import React, { useEffect, useState } from "react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";

interface Permission {
  key: string;
  label: string;
  description?: string;
}

const RolesPermissions: React.FC = () => {
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [mapping, setMapping] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string>("");

  useAutoDismiss(message, setMessage, "", 5000);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(getApiUrl("superadmin/rbac/roles-permissions"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRoles(res.data.roles || []);
        setPermissions(res.data.permissions || []);
        setMapping(res.data.mapping || {});
      } catch (error: any) {
        setMessage(error.response?.data?.message || "Failed to load RBAC data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const togglePermission = (role: string, permKey: string) => {
    setMapping(prev => {
      const current = new Set(prev[role] || []);
      if (current.has(permKey)) current.delete(permKey);
      else current.add(permKey);
      return { ...prev, [role]: Array.from(current) };
    });
  };

  const save = async () => {
    try {
      setSaving(true);
      setMessage("");
      const token = localStorage.getItem("token");
      await axios.put(
        getApiUrl("superadmin/rbac/roles-permissions"),
        { mappings: mapping },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage("Permissions updated successfully.");
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Failed to update permissions.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-slate-500">Loading permissions...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 lg:p-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Roles & Permissions</h1>
            <p className="text-slate-500 text-sm">Manage global RBAC mappings.</p>
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-semibold hover:bg-slate-800 transition disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {message && (
          <div className="mb-6 p-3 rounded-xl bg-slate-100 text-slate-700 text-sm">{message}</div>
        )}

        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-slate-50/60">
                <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">Permission</th>
                {roles.map(role => (
                  <th key={role} className="px-6 py-4 text-xs font-bold text-slate-400 uppercase">
                    {role}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {permissions.map((perm) => (
                <tr key={perm.key} className="hover:bg-slate-50/40">
                  <td className="px-6 py-4">
                    <div className="font-semibold text-slate-700">{perm.label}</div>
                    <div className="text-xs text-slate-400">{perm.key}</div>
                  </td>
                  {roles.map(role => {
                    const checked = (mapping[role] || []).includes(perm.key);
                    return (
                      <td key={`${role}-${perm.key}`} className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePermission(role, perm.key)}
                          className="h-4 w-4 accent-teal-600"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissions;
